import { useEffect, useRef, useState } from "react";
import { Search, X, User, Trophy, ClipboardList, PenLine, Users, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ players: [], practices: [], games: [], reflections: [], teams: [], pages: [] });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) { setQ(""); setResults({ players: [], practices: [], games: [], reflections: [], teams: [], pages: [] }); return; }
    setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  useEffect(() => {
    if (!q || q.length < 2) { setResults({ players: [], practices: [], games: [], reflections: [], teams: [], pages: [] }); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try { const r = await api.get("/search", { params: { q } }); setResults(r.data); } catch {}
      setBusy(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); nav(path); };

  const has = Object.values(results).some(v => v.length > 0);

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="global-search-open" aria-label="Search" className="p-2 rounded-full hover:bg-gray-100 transition-colors">
        <Search className="w-[18px] h-[18px] text-gray-600" />
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm animate-fade-in flex items-start justify-center pt-16 px-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="global-search-panel">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
              <Search className="w-5 h-5 text-gray-400" />
              <input
                ref={inputRef}
                value={q}
                onChange={e => setQ(e.target.value)}
                data-testid="global-search-input"
                placeholder="Search players, practices, games, reflections…"
                className="flex-1 outline-none text-base bg-transparent placeholder:text-gray-400"
              />
              <kbd className="hidden md:inline text-[10px] font-semibold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">ESC</kbd>
              <button onClick={() => setOpen(false)} className="p-1 text-gray-400 hover:text-gray-800"><X className="w-4 h-4" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {q.length < 2 ? (
                <div className="p-10 text-center text-sm text-gray-500">
                  <div className="text-2xl mb-2">🔎</div>
                  Type at least 2 characters to search across everything you've coached.
                </div>
              ) : busy && !has ? (
                <div className="p-10 text-center text-sm text-gray-400">Searching…</div>
              ) : !has ? (
                <div className="p-10 text-center text-sm text-gray-500">No matches for "<span className="font-semibold text-gray-800">{q}</span>"</div>
              ) : (
                <div className="py-2">
                  <Section title="Jump to" icon={Compass}>{results.pages.map(p => (
                    <Row key={p.id} onClick={() => go(p.path)} testid={`gs-page-${p.id}`} label={p.name} sub={p.path} />
                  ))}</Section>
                  <Section title="Players" icon={User}>{results.players.map(p => (
                    <Row key={p.id} onClick={() => go(`/players/${p.id}`)} testid={`gs-player-${p.id}`} label={p.name} sub={`#${p.number || "—"} · ${p.preferred_position || ""}`} />
                  ))}</Section>
                  <Section title="Practices" icon={ClipboardList}>{results.practices.map(p => (
                    <Row key={p.id} onClick={() => go(`/practices/${p.id}`)} testid={`gs-practice-${p.id}`} label={p.focus || "Untitled practice"} sub={`${p.duration_min || 90}m · ${(p.date || "").slice(0, 10)}`} />
                  ))}</Section>
                  <Section title="Games" icon={Trophy}>{results.games.map(g => (
                    <Row key={g.id} onClick={() => go(`/games/${g.id}`)} testid={`gs-game-${g.id}`} label={`vs ${g.opponent}`} sub={`${g.our_score}–${g.opp_score} · ${(g.date || "").slice(0, 10)}`} />
                  ))}</Section>
                  <Section title="Reflections" icon={PenLine}>{results.reflections.map(r => (
                    <Row key={r.id} onClick={() => go("/journey")} testid={`gs-refl-${r.id}`} label={r.question || "Reflection"} sub={(r.answer || "").slice(0, 80)} />
                  ))}</Section>
                  <Section title="Teams" icon={Users}>{results.teams.map(t => (
                    <Row key={t.id} onClick={() => go("/teams")} testid={`gs-team-${t.id}`} label={t.name} sub={t.age_group} />
                  ))}</Section>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, icon: Icon, children }) {
  const arr = Array.isArray(children) ? children.filter(Boolean) : [children].filter(Boolean);
  if (arr.length === 0) return null;
  return (
    <div className="pb-2">
      <div className="px-5 pt-3 pb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
        <Icon className="w-3 h-3" /> {title}
      </div>
      {arr}
    </div>
  );
}
function Row({ onClick, label, sub, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full text-left px-5 py-2.5 hover:bg-gray-50 transition-colors">
      <div className="text-sm font-semibold text-gray-900 truncate">{label}</div>
      {sub && <div className="text-xs text-gray-500 truncate mt-0.5">{sub}</div>}
    </button>
  );
}
